import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  DeleteCompanyCarrierActions as DeleteActions,
  CreateCompanyCarrierActions as CreateActions,
} from 'store/ducks/companyCarriers';
import { useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import * as Yup from 'yup';
import * as S from './styles';
import { CompanyCarrier } from 'contracts/Companies';
import { Formatter } from 'utils';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  onUpdate?: () => void;
  companyId: string;
}

interface ICarriersManager
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

interface ItemProps {
  carrier: CompanyCarrier['carrier'];
  id: number;
}

const CarriersManager: ICarriersManager = forwardRef<Ref, Props>(
  ({ companyId, onUpdate }, ref) => {
    const { data: company } = useSelector(
      (state: RootState) => state.fetchCompany
    );

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();

    /**
     * this component will not fetch carriers here,
     * we lifted it to a upper level (DocksManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingCarriers, data: carriers } = useSelector(
      (state: RootState) => state.listCarriers
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createCompanyCarrier
    );

    const { id: deletingId, loading: deleting } = useSelector(
      (state: RootState) => state.deleteCompanyCarrier
    );

    /**
     * we will also filter the list of carriers to only show
     * the ones that are not already bound to the dock
     * */

    const carrierOptions: SelectOption[] | undefined = useMemo(() => {
      if (!carriers?.length) return undefined;

      return carriers
        .filter((carrier) =>
          company?.companyCarriers
            ? !company.companyCarriers.find(
                (companyCarrier) => companyCarrier.carrier.id === carrier.id
              )
            : true
        )
        .map((carrier) => ({
          value: carrier.id,
          label: `${carrier.tradeName} - ${Formatter.document(
            carrier.document,
            carrier.documentType
          )}`,
        }));
    }, [carriers, company]);

    const onDelete = useCallback(
      (id?: number) => dispatch(DeleteActions.request(id, onUpdate)),
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});
          const schema = Yup.object().shape({
            carrierId: Yup.number()
              .typeError('Selecione a transportadora')
              .required('Selecione a transportadora'),
          });
          const validData = await schema.validate(data, {
            abortEarly: false,
          });
          dispatch(
            CreateActions.request({ ...validData, companyId }, onSuccess)
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, handleFormErrors, onSuccess, companyId]
    );

    const Item = useCallback(
      ({ carrier, id }: ItemProps): JSX.Element => {
        const {
          addressCity,
          addressState,
          document,
          id: carrierId,
          tradeName,
        } = carrier;

        return (
          <S.ListItem>
            <S.Column>{carrierId}</S.Column>
            <S.Column>{tradeName}</S.Column>
            <S.Column>{Formatter.cnpj(document)}</S.Column>
            <S.Column>{addressCity}</S.Column>
            <S.Column>{addressState}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === carrierId || deleting}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === carrierId ? (
                  <S.ActivityIndicator />
                ) : (
                  <S.TrashIcon />
                )}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete, deleting]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="carrierId"
              label="Transportadora"
              options={carrierOptions}
              isLoading={loadingCarriers}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
            <S.Button type="submit" disabled={creating}>
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {!company?.companyCarriers?.length ? (
          <S.EmptyListPlaceholder>
            Esse armazém ainda não possui restrições para transportadoras
          </S.EmptyListPlaceholder>
        ) : (
          <>
            <S.ListHeader>
              <div>ID</div>
              <div>NOME</div>
              <div>CNPJ</div>
              <div>CIDADE</div>
              <div>ESTADO</div>
            </S.ListHeader>
            {company?.companyCarriers?.map((props) => (
              <Item {...props} key={props.id} />
            ))}
          </>
        )}
      </S.Container>
    );
  }
);

export default CarriersManager;
